import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types';
import {
  BulletList,
  Button,
  Icon,
  ListItem, radius,
  space,
  SpaceBetween,
  tk,
  tok,
  Typography,
} from '@sunwebgroup/ui-components';
import { css, styled } from 'styled-components';
import * as React from 'react';
import { useService } from '@/scopes/checkout/domain/hooks';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { sub } from 'date-fns';
import {
  carIsBeingUpgraded, carToBeUpgraded,
  useCarRentalServiceStore,
  editingCar
} from '@/scopes/checkout/components/services/CarRentalFilteringService/store';
import { useIntl } from '@/intl';
import { removeCar, upgradeCar } from '@/scopes/checkout/domain/actions';
import { createCarEvent } from '@/scopes/checkout/domain/analytics';
import type { FilterableCarItem } from '@/scopes/checkout/filterableCars/domain/types';

export function CarOption({ isSelected, ...option }: CheckoutCarRentalServiceOption & { isSelected: boolean }) {
  const { service} = useService(ServiceType.CarRentalFiltering);

  if (!service) return null;

  return (
    <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
      <CarCard $selected={isSelected} as="div">
        {isSelected && <YourSelectionBadge $state="success">Your Selection</YourSelectionBadge>}
        <ListItem.Content>
          <CarOptionCardView isSelected={isSelected} {...option} />
        </ListItem.Content>
      </CarCard>
    </SpaceBetween>
  )
}

export function CarOptionCardView({ isSelected,  ...option }: CheckoutCarRentalServiceOption & { isSelected: boolean }) {
  const {id: carId, price, driverId, canBeRemoved, isIncluded, canBeUpgraded, equipments, selectedEquipments} = option;
  const {sendEvent} = useAnalytics();
  const { isDesktopSm } = useBreakpoints();
  const { service, setServiceValue} = useService(ServiceType.CarRentalFiltering);
  const { configurations } = service || {};

  const { driverId: currentDriverIdSelected } = option;
  const driver = currentDriverIdSelected ? service?.participants[currentDriverIdSelected] : null;

  const defaultDriverBirthdate = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver, days: 1 });

  const { startEditingCar, endEditingCar, setCarIsBeingUpgraded, setCarToBeUpgraded, setShowExtraCars, setCarToBeSelected} = useCarRentalServiceStore();
  const isCarBeingUpgraded = useCarRentalServiceStore(carIsBeingUpgraded);
  const carValueToBeUpgraded = useCarRentalServiceStore(carToBeUpgraded);

  const intl = useIntl();

  const currentEditingCar = useCarRentalServiceStore(editingCar(carValueToBeUpgraded))

  const handleSelectClick = React.useCallback((carValue: string) => {
      if (!carValue) return;
      if(!service) return;

      if(isCarBeingUpgraded) {
        if (currentEditingCar) {
          const {
            driverId: currentSelectedDriverId
          } = currentEditingCar

          setServiceValue(upgradeCar({ value: carValue, participantId: currentSelectedDriverId ?? '' })(service));
        }

        setCarIsBeingUpgraded(false)
        setCarToBeUpgraded('');
        endEditingCar(carId);

        sendEvent(
          createCarEvent({
            result: 'upgrade',
            type: carValue,
            description: 'upgrade button clicked',
            element: 'button',
          })
        )
      }

      setCarToBeSelected(carId)
      setShowExtraCars(false)

      if (!driverId && !currentEditingCar) {
        startEditingCar(carId, {
          driverId: null,
          driverBirthDate: defaultDriverBirthdate,
          driverHasCreditCard: false,
          bookedEquipments: [],
          isEditing: false,
        });

      }


    },
    [currentEditingCar, endEditingCar, isCarBeingUpgraded, setCarToBeUpgraded, setShowExtraCars, defaultDriverBirthdate, driverId, sendEvent, setCarToBeSelected, setCarIsBeingUpgraded, setServiceValue, startEditingCar, carId, service]
  );

  const handleRemoveClick = React.useCallback((carValue: string) => {
      if (!carValue) return;
      if(!service) return;
      if (!driverId) {
        setCarToBeSelected('');
      }
      if (driverId) {
        setServiceValue(removeCar({participantId: driverId ?? ''})(service));
        sendEvent(
          createCarEvent({
            result: 'remove',
            type: carValue,
            description: 'remove button clicked',
            element: 'button',
          })
        )
      }


    },
    [sendEvent, driverId, setServiceValue, service]
  );

  const handleChangeClick = React.useCallback((carValue: string) => {
      if (!carValue) return;
      if(!service) return;
      setCarIsBeingUpgraded(true);
      setCarToBeUpgraded(carValue);

      if (!driver) return;
      startEditingCar(
        carValue,
        {
          driverId,
          driverBirthDate: driver.birthdate ? new Date(driver.birthdate) : new Date(),
          driverHasCreditCard: true,
          bookedEquipments: equipments.filter(({id}) => selectedEquipments?.includes(id)).map(({id}) => id),
          isEditing: true
        });

      sendEvent(
        createCarEvent({
          result: 'upgrade',
          type: carValue,
          description: 'upgrade button clicked',
          element: 'button',
        })
      )
    },
    [driver, driverId, equipments, selectedEquipments, setCarToBeUpgraded, startEditingCar, sendEvent, setCarIsBeingUpgraded, service]
  );

  if (!service) return;
  return (
    <CarContent>
      <CarCardBody {...option}/>
      <CarCardFooter>
        <Price as="span" $variant="body-lg-bold">
          {intl.formatTruncatedCurrency(price)}
        </Price>
        <ActionButtons>
          {!isSelected && (
            <Button
              $hierarchy="secondary"
              $withoutColor
              onClick={() => handleSelectClick(carId)}
              $size='small'>
              {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_select_car_text'})}
            </Button>
          )}
          {isSelected &&(
            <>
              {canBeRemoved && !isIncluded && (
                <Button
                  $hierarchy="secondary"
                  $withoutColor
                  onClick={() => handleRemoveClick(carId)}
                  $size='small'>
                  {isDesktopSm && <Icon glyph="IconCrossMedium" /> }
                  {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_remove_car_text'})}
                </Button>
              )}
              {canBeUpgraded && (
                <Button
                  $hierarchy="secondary"
                  $withoutColor
                  onClick={() => handleChangeClick(carId)}
                  $size='small'>
                  {isDesktopSm && <Icon glyph="IconPencil" /> }
                  {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_change_car_text'})}
                </Button>
              )}
            </>
          )}

        </ActionButtons>
      </CarCardFooter>
    </CarContent>
  );
}

export const CarCardBody = ({ ...option }: CheckoutCarRentalServiceOption | FilterableCarItem) => {
  const {id, label, features, extraFeatures = [], conditionsUrl, image, carClass, availableOnRequest} = option;
  const intl = useIntl();
  const {sendEvent} = useAnalytics();
  const { isDesktopSm } = useBreakpoints();

  const handleConditionsLinkClick = () => {
    sendEvent(
      createCarEvent({
        result: 'conditions page opened',
        type: id,
        description: 'Car conditions link clicked',
        element: 'button',
      })
    )
  }

  return (
    <>
      {image && <CarImage src={image} alt={label} width={300} height={300} />}
      <CarHeader direction="y" spacing={tk('spacing_2xs')}>
        <CarClass $variant="body-sm">{carClass}</CarClass>
        {availableOnRequest && (
          <AvailableOnRequestBadge $state="info">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_available_on_request_label' })}
          </AvailableOnRequestBadge>
        )}
        <CarName>{label}</CarName>
        <CarFeatures $size="small" $variant="multiline">
          {features.map(({ glyph, text }) => (
            <CarRentalUsp key={glyph} $icon={glyph}>
              {text}
            </CarRentalUsp>
          ))}
        </CarFeatures>
      </CarHeader>

      <CarInformationDetails direction="y" spacing={tk('spacing_2xs')}>
        <BulletList.Root $size="small" $variant="multiline" $type={isDesktopSm ? 'inline' : 'vertical'}>
          {extraFeatures.map(({ glyph, text }) => (
            <CarRentalUsp key={glyph} $icon={glyph}>
              {text}
            </CarRentalUsp>
          ))}
        </BulletList.Root>
        <Link
          onClick={handleConditionsLinkClick}
          href={conditionsUrl} target="_blank"
          title={intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_car_conditions_text'})}>
          <Typography>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_filtering_car_conditions_text'})}</Typography>
        </Link>
      </CarInformationDetails>
    </>
  )
}

const CarContent = styled.div`
  display: grid;
  grid-template-areas:
    "image head"
    "info info"
    "footer footer";
  grid-template-rows: repeat(3, auto);
  grid-template-columns: 120px auto;
  width: 100%;
  @media ${tk('mq_desktop_md')} {
    padding: ${space('primitives_space_12')};
    grid-template-areas:
      "image head footer"
      "image info footer";
    grid-template-rows: repeat(2, auto);
    grid-template-columns: 160px auto 120px;
  }
`;

export const CarImage = styled.img`
  width: 100%;
  height: auto;
  grid-area: image;
`;
export const CarHeader = styled(SpaceBetween)`
  flex: 1;
  grid-area: head;
  padding: ${space('primitives_space_12')};
`
const CarName = styled(ListItem.Label)`
  ${Typography.modVariant('body-lg-bold')}
  color: ${tok('semantics_text_color')};
`;
export const CarInformationDetails = styled(SpaceBetween)`
  border-top: 2px solid ${tk('color_semantic_border_subdued')};
  grid-area: info;
  padding-top: ${space('primitives_space_12')};
  padding-left: ${space('primitives_space_12')};
  width: 100%;
  flex: 1;
`;
const CarClass = styled(Typography)`
  color: ${tk('color_semantic_text')};
`;
const CarFeatures = styled(BulletList.Root)`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${space('primitives_space_8')};
`;
const CarRentalUsp = styled(BulletList.Item)`
  color: ${tk('color_semantic_text')};
`;
const CarCardFooter = styled.div`
  background-color: ${tk('color_semantic_bg_subdued')};
  width: 100%;
  grid-area: footer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${space('primitives_space_12')};
  padding: ${space('primitives_space_12')};

  @media ${tk('mq_desktop_md')} {
    background-color: transparent;
    flex-direction: column;
    justify-content: center;
    padding: ${space('primitives_space_0')};
    padding-left: ${space('primitives_space_12')};
  }
`;
export const Price = styled(Typography)`
  display: flex;
  align-items: center;
  color: ${tok('semantics_text_color')};
`;
const AvailableOnRequestBadge = styled(ListItem.Badge)`
  width: fit-content;
`;
const Link = styled.a`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  text-decoration: underline;
  color: ${tk('color_semantic_text')};
`;
const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;

  gap: ${space('primitives_space_12')};

  @media ${tk('mq_desktop_md')} {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    ${Button} {
      width: 100%;
    }
  }
`;
export const CarCard = styled(ListItem.Root)<{ $selected: boolean }>`
  flex-direction: column;
  padding: ${space('primitives_space_0')};
  ${({ $selected }) => $selected && modSelected()}
`
const YourSelectionBadge = styled(ListItem.Badge)`
  width: 100%;
  border-radius: ${radius('primitives_corner_radius_none')};
  border: 1px solid ${tk('color_semantic_border_success')};
`;

const modSelected = () => css`
  border-color: ${tk('color_semantic_border_success')};
`;
